"use client";
import React from "react";
import { DrupalNode } from "next-drupal";
import { motion } from "framer-motion";
import Link from "next/link";
import { config } from "@/lib/config";
import Banner from "@/components/sections/Banner";
import FindOutSection from "@/components/sections/FindOutSection";
import SectorResourceList from "@/components/sections/SectorResourceList";
import PartnerCard from "@/components/sections/PartnerCard";

interface SupportScreenProps {
  pageData: DrupalNode;
  iterableSectors: { [key: string]: DrupalNode[] }[];
}

const SupportScreen: React.FC<SupportScreenProps> = ({
  pageData,
  iterableSectors,
}) => {
  const {
    title = "",
    field_support_sub_title = "",
    field_sector_section_title = "",
    field_economy_wide_title = "",
    field_economy_wide_cards = [],
    field_resource_and_tools_title = "",
    field_resource_and_tools_cards = [],
    field_support_footer_section = {} as DrupalNode,
    field_support_training_section = {},
  } = pageData;
  return (
    <>
      <div className="pt-20">
        <Banner
          title={title}
          description={field_support_sub_title}
          isRounded
          backgroundImg="/static/images/support-banner.webp"
        />
        <div
          id="economy-wide-resources-partners"
          className="py-[100px] bg-mapGray mobileMax:pt-12 lieExactTab:py-10 betweenMobileTab:py-10 overflow-hidden relative"
        >
          {/* title section */}
          <div className="mini-container relative z-[2] h-full flex flex-col items-center justify-center mobileMax:block">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation mobileMax:pt-[60px] bg-[linear-gradient(to_top,#8EBCE5,#11375C)] font-semibold text-clip tracking-tight desktop:mb-[70px] mt-0 leading-tight text-center text-numans text-[45px] mobileMax:text-[32px] mb-10"
            >
              {field_resource_and_tools_title}
            </motion.h2>
            <div className="flex flex-wrap justify-center box-border w-full">
              {field_resource_and_tools_cards?.map(
                (card: DrupalNode, index: number) => {
                  return (
                    <motion.div
                      key={index}
                      className="remove-animation-fluctuation px-[15px] mb-[30px] w-[33%] min-h-[280px] mobileMax:w-full mobileMax:px-0 mobileMax:w-full mobileMax:min-h-full"
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                    >
                      <PartnerCard
                        img={`${config.apiBase}${card?.field_image_icon?.uri?.url}`}
                        title={card?.title || ""}
                        buttonText={card?.field_button_text}
                        link={card?.field_link}
                      />
                    </motion.div>
                  );
                },
              )}
            </div>
          </div>
        </div>
        <div className="pt-10 pb-[140px] bg-graybg betweenMobileTab:pb-20 mobileMax:pb-10 mobileMax:pt-0">
          <div className="mini-container">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0,
              }}
              className="remove-animation-fluctuation mt-[82px] mobileMax:pt-10 mobileMax:mt-0 bg-[linear-gradient(to_top,#8EBCE5,#11375C)] font-semibold tracking-tight desktop:leading-[85px] text-center text-clip text-[48px] leading-[60px] mb-14 desktop:mb-[100px] mobileMax:text-[28px] mobileMax:leading-normal mobileMax:mb-8"
            >
              {field_sector_section_title}
            </motion.h3>
            {/* cards Sector resources and partners*/}
            {iterableSectors.map((sector, index) => {
              return (
                <div
                  id="sector-resources-and-partners"
                  key={index}
                  className="flex items-start w-full justify-between desktop:flex-nowrap flex-wrap mb-[30px] border-b border-[#aec9f1] last:border-0"
                >
                  <div className="desktop:w-[30%] w-full mb-5">
                    <motion.h3
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0,
                      }}
                      className="remove-animation-fluctuation text-clip bg-[#11375C] text-[32px] tracking-tight leading-10 mr-5 text-numans mobileMax:text-[25px]   desktop:px-0 px-5 mobileMax:px-5 mobileMax:leading-8 mobileMax:mr-0"
                    >
                      {Object.keys(sector)[0]}:
                    </motion.h3>
                  </div>
                  <SectorResourceList list={Object.values(sector)[0]} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* last section Economy-wide resources & partners */}
      <div
        id="economy-wide-resources-partners"
        className="relative bg-[#003350]"
      >
        <div className="overflow-hidden relative min-h-[560px] pt-[140px] pb-[100px] box-border mobileMax:py-10">
          {/* title section */}
          <div className="mini-container h-full flex flex-col items-center justify-center mobileMax:block">
            <div className="mx-auto">
              <motion.h2
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0,
                }}
                className="remove-animation-fluctuation mobileMax:pt-[60px] bg-[#48DBB2] text-clip tracking-tight font-semibold desktop:mb-[70px] mt-0 leading-tight text-center text-numans text-[45px] mobileMax:text-[32px] mb-10"
              >
                {field_economy_wide_title}
              </motion.h2>
              <div className="flex flex-wrap justify-center box-border">
                {field_economy_wide_cards?.map(
                  (card: DrupalNode, index: number) => {
                    return (
                      <motion.div
                        key={index}
                        className="remove-animation-fluctuation px-[15px] mb-[30px] w-[33%] min-h-[280px] mobileMax:w-full mobileMax:px-0 mobileMax:w-full mobileMax:min-h-full"
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0,
                        }}
                      >
                        <PartnerCard
                          img={`${config.apiBase}${card?.field_image_icon?.uri?.url}`}
                          title={card?.title || ""}
                          buttonText={card?.field_button_text}
                          link={card?.field_link}
                        />
                      </motion.div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </div>
        {/* bottom raining cards AND training cards  */}
        <div className="pt-[100px] pb-[90px] bg-white relative mobileMax:py-10 betweenMobileTab:py-12 overflow-hidden">
          <div className="mini-container relative z-[1] flex items-center justify-between mobileMax:flex-col">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                type: "spring",
                duration: 2.5,
              }}
              className="w-[50%] mobileMax:w-full mobileMax:order-2 mobileMax:mt-5"
            >
              <h4 className="mb-6 bg-[linear-gradient(to_top,#8EBCE5,#11375C)] font-semibold tracking-tight leading-normal text-left text-clip text-[48px] mobileMax:text-[28px] mobileMax:mb-3">
                {field_support_training_section?.title}
              </h4>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    field_support_training_section?.field_content?.processed,
                }}
                className="--font-poppins text-left text-medium text-cardText mb-4 leading-8 mobileMax:text-xsmall mobileMax:leading-normal"
              />
              <div>
                <Link
                  href={"/trainings"}
                  className="h-[46px] rounded-md bg-[linear-gradient(to_right,#316FA8,#003350)] px-[30px] flex max-w-[160px] items-center justify-center mt-10 text-[#fff] font-mediums --font-poppins text-xsmall"
                >
                  {field_support_training_section?.field_button_text}
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                type: "spring",
                duration: 2.5,
              }}
              className="w-[50%] mobileMax:w-full mobileMax:order-1"
            >
              <motion.div
                className="max-h-[480px] rounded-[25px] overflow-hidden no-bottom-space"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  type: "spring",
                  duration: 1.8,
                }}
              >
                <img
                  src={`${config.apiBase}${
                    field_support_training_section?.field_image_icon?.uri
                      ?.url || ""
                  }`}
                  alt="boardImg"
                  className="h-full w-full object-cover transform transition-transform duration-500 hover:scale-105"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
      <FindOutSection data={field_support_footer_section} />
    </>
  );
};

export default SupportScreen;
